import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';

const C = {
  navy:'#0D1B2A', teal:'#00B4D8', grey:'#6B7280', border:'#E5E7EB',
  bg:'#F0F7FA', red:'#EF4444', green:'#10B981', orange:'#F59E0B', purple:'#8B5CF6',
};
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const HOURS = [8,9,10,11,12,13,14,15,16,17,18,19,20];

const STATUS_COLOR = { pending:'#F59E0B', confirmed:'#10B981', cancelled:'#EF4444', completed:'#6B7280' };
const STATUS_BG    = { pending:'#FEF3C7', confirmed:'#D1FAE5', cancelled:'#FEE2E2', completed:'#F3F4F6' };

const PLATFORMS = [
  { value:'google_meet', label:'Google Meet', icon:'📹' },
  { value:'zoom',        label:'Zoom',        icon:'🎥' },
  { value:'teams',       label:'Teams',       icon:'💼' },
  { value:'skype',       label:'Skype',       icon:'📞' },
];

function getWeekDates(offsetWeeks = 0) {
  const today = new Date();
  const day = today.getDay() || 7;
  const monday = new Date(today);
  monday.setDate(today.getDate() - day + 1 + offsetWeeks * 7);
  return DAYS.map((_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d.toISOString().split('T')[0];
  });
}

function fmt12(h) {
  if (h === 0) return '12:00 AM';
  if (h < 12) return `${h}:00 AM`;
  if (h === 12) return '12:00 PM';
  return `${h-12}:00 PM`;
}

export default function AdminBookings() {
  const [bookings,  setBookings]  = useState([]);
  const [tutors,    setTutors]    = useState([]);
  const [tenant,    setTenant]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null);
  const [view,      setView]      = useState('list');
  const [weekOff,   setWeekOff]   = useState(0);
  const [saving,    setSaving]    = useState(false);
  const [editMode,  setEditMode]  = useState(false);
  const [editData,  setEditData]  = useState({});
  const [msg,       setMsg]       = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [assignModal, setAssignModal] = useState(null);    // { booking, suggestions, assigned_ids }
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');
  const [assignSkill,  setAssignSkill]  = useState('');
  const [assignSelected, setAssignSelected] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/api/admin/dashboard').catch(() => null),
      api.get('/api/admin/bookings').catch(() => null),
      api.get('/api/admin/tutors').catch(() => null),
    ]).then(([dashRes, bookRes, tutRes]) => {
      setTenant(dashRes?.data?.tenant);
      setBookings(bookRes?.data?.bookings || []);
      setTutors(tutRes?.data?.tutors || []);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const weekDates = getWeekDates(weekOff);
  const today = new Date().toISOString().split('T')[0];

  const filtered = filterStatus === 'all'
    ? bookings
    : bookings.filter(b => b.status === filterStatus);

  // Build slot map
  const slotMap = {};
  bookings.forEach(b => {
    const key = `${b.booking_date}_${b.time_slot}`;
    if (!slotMap[key]) slotMap[key] = [];
    slotMap[key].push(b);
  });

  const pending   = bookings.filter(b => b.status === 'pending');
  const upcoming  = bookings.filter(b => b.booking_date >= today && b.status === 'confirmed');
  const activeTutors = [...new Set(bookings.filter(b => b.booking_date >= weekDates[0] && b.booking_date <= weekDates[6]).map(b => b.tutor_name))].length;

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 3000);
  };

  const openAssignModal = async (booking) => {
    try {
      const [sugRes, curRes] = await Promise.all([
        api.get(`/api/admin/bookings/${booking.id}/materials-suggestions`).catch(() => null),
        api.get(`/api/admin/bookings/${booking.id}/assigned-materials`).catch(() => null),
      ]);
      const suggestions = sugRes?.data?.materials || [];
      const currentIds  = (curRes?.data?.materials || []).map(m => m.material_id);
      setAssignModal({ booking, suggestions, student_level: sugRes?.data?.student_level || '' });
      setAssignSelected(currentIds);
      setAssignSearch('');
      setAssignSkill('');
    } catch (e) {
      flash('Failed to load materials', false);
    }
  };

  const saveAssignment = async () => {
    if (!assignModal) return;
    setAssignSaving(true);
    try {
      await api.post(`/api/admin/bookings/${assignModal.booking.id}/assigned-materials`, {
        material_ids: assignSelected,
      });
      flash(`${assignSelected.length} material(s) assigned`);
      setAssignModal(null);
    } catch (e) {
      flash(e.response?.data?.error || 'Save failed', false);
    } finally {
      setAssignSaving(false);
    }
  };

  const toggleAssign = (id) => {
    setAssignSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const openEdit = (booking) => {
    setSelected(booking);
    setEditData({
      tutor_id:       booking.tutor_id || '',
      status:         booking.status,
      delivery_mode:  booking.delivery_mode || 'physical',
      video_link:     booking.video_link || '',
      video_platform: booking.video_platform || 'google_meet',
      duration_minutes: booking.duration_minutes || 90,
      notes:          booking.notes || '',
    });
    setEditMode(true);
  };

  const closeModal = () => { setSelected(null); setEditMode(false); setEditData({}); };

  const saveEdit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await api.put(`/api/admin/bookings/${selected.id}`, editData);
      const updated = res.data?.booking;
      setBookings(prev => prev.map(b => b.id === updated.id ? updated : b));
      setSelected(updated);
      setEditMode(false);
      flash('Booking updated ✓');
    } catch (e) {
      flash(e.response?.data?.error || 'Save failed', false);
    } finally {
      setSaving(false);
    }
  };

  const quickAction = async (booking, action) => {
    setSaving(true);
    try {
      const res = await api.post(`/api/admin/bookings/${booking.id}/${action}`);
      const updated = res.data?.booking;
      setBookings(prev => prev.map(b => b.id === updated.id ? updated : b));
      if (selected?.id === booking.id) setSelected(updated);
      flash(action === 'confirm' ? 'Booking confirmed ✓' : 'Booking cancelled');
    } catch (e) {
      flash(e.response?.data?.error || 'Action failed', false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout role="tenant_admin" tenantColor={tenant?.primary_colour} tenantName={tenant?.school_name}>
      {/* Toast */}
      {msg && (
        <div style={{ position:'fixed', top:20, right:20, zIndex:9999, padding:'12px 20px', borderRadius:10,
          background: msg.ok ? '#D1FAE5' : '#FEE2E2', color: msg.ok ? '#065F46' : '#991B1B',
          fontWeight:600, fontSize:13, boxShadow:'0 4px 16px rgba(0,0,0,0.15)' }}>
          {msg.text}
        </div>
      )}

      <div style={{ marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4, flexWrap:'wrap', gap:8 }}>
          <h2 style={{ margin:0, fontSize:22, fontWeight:700, color:C.navy }}>Class Bookings</h2>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {['list','grid'].map(v => (
              <button key={v} onClick={() => setView(v)} style={{
                padding:'6px 14px', borderRadius:8, border:'1px solid',
                borderColor: view===v ? C.teal : C.border,
                background: view===v ? C.bg : '#fff',
                color: view===v ? C.teal : C.grey,
                cursor:'pointer', fontSize:13, fontWeight:600,
              }}>{v === 'list' ? '☰ List' : '⊞ Grid'}</button>
            ))}
          </div>
        </div>
        <p style={{ margin:0, color:C.grey, fontSize:14 }}>Manage all student bookings — confirm, assign tutors, add video links</p>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:12, marginBottom:24 }}>
        {[
          { label:'Pending Review', value:pending.length, sub:'need action', icon:'⏳', color:'#F59E0B', bg:'#FEF3C7' },
          { label:'Confirmed', value:upcoming.length, sub:'upcoming sessions', icon:'✅', color:C.green, bg:'#D1FAE5' },
          { label:'Active Tutors', value:activeTutors, sub:'this week', icon:'👩‍🏫', color:C.teal, bg:C.bg },
          { label:'Total Bookings', value:bookings.length, sub:'all time', icon:'📅', color:C.purple, bg:'#F5F3FF' },
        ].map(s => (
          <div key={s.label} style={{ background:'#fff', borderRadius:12, padding:'14px 16px', boxShadow:'0 1px 4px rgba(0,0,0,0.08)', borderLeft:`4px solid ${s.color}` }}>
            <div style={{ fontSize:24, marginBottom:4 }}>{s.icon}</div>
            <div style={{ fontSize:24, fontWeight:800, color:C.navy }}>{s.value}</div>
            <div style={{ fontWeight:600, fontSize:13, color:C.navy }}>{s.label}</div>
            <div style={{ fontSize:11, color:C.grey }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Pending Alert */}
      {pending.length > 0 && (
        <div style={{ background:'#FEF3C7', border:'1px solid #F59E0B', borderRadius:10, padding:'12px 16px', marginBottom:20, display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:20 }}>⚠️</span>
          <div style={{ flex:1 }}>
            <strong style={{ color:'#92400E' }}>{pending.length} booking{pending.length>1?'s':''} awaiting confirmation</strong>
            <div style={{ fontSize:12, color:'#B45309', marginTop:2 }}>Review and confirm these bookings to notify students. Assign a tutor and video link before confirming.</div>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        {['all','pending','confirmed','completed','cancelled'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} style={{
            padding:'5px 12px', borderRadius:20, border:'1px solid',
            borderColor: filterStatus===s ? C.teal : C.border,
            background: filterStatus===s ? C.bg : '#fff',
            color: filterStatus===s ? C.teal : C.grey,
            cursor:'pointer', fontSize:12, fontWeight:600, textTransform:'capitalize',
          }}>
            {s === 'all' ? `All (${bookings.length})` : `${s} (${bookings.filter(b=>b.status===s).length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:40, color:C.grey }}>Loading bookings...</div>
      ) : filtered.length === 0 ? (
        <div style={{ background:'#fff', borderRadius:12, padding:40, textAlign:'center', boxShadow:'0 1px 4px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📭</div>
          <div style={{ fontSize:16, fontWeight:600, color:C.navy, marginBottom:8 }}>
            {bookings.length === 0 ? 'No bookings yet' : 'No bookings in this filter'}
          </div>
          <div style={{ color:C.grey, fontSize:14 }}>
            {bookings.length === 0 ? 'Students can book group lessons from their dashboard.' : 'Try changing the filter above.'}
          </div>
        </div>
      ) : view === 'list' ? (
        /* ── List View ── */
        <div style={{ background:'#fff', borderRadius:12, boxShadow:'0 1px 4px rgba(0,0,0,0.08)', overflow:'hidden' }}>
          {filtered.map((b, i) => (
            <div key={b.id} style={{
              display:'flex', alignItems:'center', gap:12, padding:'14px 20px', flexWrap:'wrap',
              borderBottom: i < filtered.length-1 ? `1px solid ${C.border}` : 'none',
              background: b.status === 'pending' ? '#FFFBEB' : '#fff',
            }}>
              {/* Date / Time */}
              <div style={{ minWidth:90 }}>
                <div style={{ fontWeight:700, fontSize:13, color:C.navy }}>{b.booking_date}</div>
                <div style={{ fontSize:12, color:C.grey }}>{fmt12(b.time_slot)} · {b.duration_minutes || 90}m</div>
              </div>

              {/* Student */}
              <div style={{ flex:1, minWidth:120 }}>
                <div style={{ fontSize:13, fontWeight:600, color:C.navy }}>👤 {b.student_name}</div>
                <div style={{ fontSize:11, color:C.grey }}>{b.plan_type || 'human'}</div>
              </div>

              {/* Tutor */}
              <div style={{ flex:1, minWidth:120 }}>
                <div style={{ fontSize:13, fontWeight:600, color:C.navy }}>👩‍🏫 {b.tutor_name || '—'}</div>
                <div style={{ fontSize:11, color:C.grey }}>Tutor</div>
              </div>

              {/* Video link status */}
              <div style={{ minWidth:100 }}>
                {b.video_link ? (
                  <span style={{ fontSize:11, background:'#D1FAE5', color:'#065F46', padding:'2px 8px', borderRadius:20, fontWeight:600 }}>🔗 Link set</span>
                ) : b.delivery_mode === 'remote' ? (
                  <span style={{ fontSize:11, background:'#FEF3C7', color:'#92400E', padding:'2px 8px', borderRadius:20, fontWeight:600 }}>⚠ No link</span>
                ) : (
                  <span style={{ fontSize:11, background:'#F3F4F6', color:C.grey, padding:'2px 8px', borderRadius:20, fontWeight:600 }}>📍 Physical</span>
                )}
              </div>

              {/* Status */}
              <span style={{
                padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600,
                background: STATUS_BG[b.status] || '#F3F4F6',
                color: STATUS_COLOR[b.status] || C.grey,
                textTransform:'capitalize',
              }}>{b.status}</span>

              {/* Actions */}
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {b.status === 'pending' && (
                  <button onClick={() => quickAction(b, 'confirm')} disabled={saving} style={{
                    padding:'5px 12px', borderRadius:8, border:'none', background:C.green, color:'#fff',
                    cursor:'pointer', fontSize:12, fontWeight:700,
                  }}>✓ Confirm</button>
                )}
                <button onClick={() => openEdit(b)} style={{
                  padding:'5px 12px', borderRadius:8, border:`1px solid ${C.border}`, background:'#fff',
                  color:C.navy, cursor:'pointer', fontSize:12, fontWeight:600,
                }}>✏️ Manage</button>
                <button onClick={() => openAssignModal(b)} style={{
                  padding:'5px 12px', borderRadius:8, border:`1px solid ${C.purple}`, background:'#F5F3FF',
                  color:C.purple, cursor:'pointer', fontSize:12, fontWeight:600,
                }}>📚 Lesson</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── Weekly Grid View ── */
        <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 1px 4px rgba(0,0,0,0.08)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
            <button onClick={() => setWeekOff(w => w-1)} style={{ padding:'4px 12px', borderRadius:8, border:`1px solid ${C.border}`, background:'#fff', cursor:'pointer', fontSize:13 }}>← Prev</button>
            <span style={{ fontWeight:600, color:C.navy, fontSize:14 }}>
              {weekDates[0]} – {weekDates[6]}
              {weekOff === 0 && <span style={{ color:C.teal, marginLeft:8, fontSize:12 }}>This week</span>}
            </span>
            <button onClick={() => setWeekOff(w => w+1)} style={{ padding:'4px 12px', borderRadius:8, border:`1px solid ${C.border}`, background:'#fff', cursor:'pointer', fontSize:13 }}>Next →</button>
            <button onClick={() => setWeekOff(0)} style={{ padding:'4px 12px', borderRadius:8, border:`1px solid ${C.teal}`, background:C.bg, color:C.teal, cursor:'pointer', fontSize:12 }}>Today</button>
          </div>

          <div style={{ overflowX:'auto' }}>
            <div style={{ minWidth:700 }}>
              <div style={{ display:'grid', gridTemplateColumns:'64px repeat(7,1fr)', gap:3, marginBottom:6 }}>
                <div/>
                {DAYS.map((d,i) => (
                  <div key={d} style={{ textAlign:'center', fontWeight:700, fontSize:12, color:weekDates[i]===today?C.teal:C.navy, padding:'4px 0', borderRadius:6, background:weekDates[i]===today?C.bg:'transparent' }}>
                    <div>{d}</div>
                    <div style={{ fontSize:10, fontWeight:400, color:C.grey }}>{weekDates[i]?.slice(5)}</div>
                  </div>
                ))}
              </div>
              {HOURS.map(h => (
                <div key={h} style={{ display:'grid', gridTemplateColumns:'64px repeat(7,1fr)', gap:3, marginBottom:3 }}>
                  <div style={{ fontSize:11, color:C.grey, paddingTop:6, textAlign:'right', paddingRight:6 }}>{fmt12(h)}</div>
                  {weekDates.map((d, di) => {
                    const slots = slotMap[`${d}_${h}`] || [];
                    const b = slots[0];
                    return (
                      <div key={di} onClick={() => b && openEdit(b)} style={{
                        height:38, borderRadius:5, cursor: b ? 'pointer' : 'default',
                        background: b ? (STATUS_BG[b.status] || '#EEF2FF') : '#F9FAFB',
                        border: `1px solid ${b ? (STATUS_COLOR[b.status]+'55') : C.border}`,
                        display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden',
                        position:'relative',
                      }}>
                        {b && <>
                          {b.status === 'pending' && (
                            <span style={{ position:'absolute', top:2, right:3, width:6, height:6, borderRadius:'50%', background:'#F59E0B' }}/>
                          )}
                          <span style={{ fontSize:9, color:STATUS_COLOR[b.status]||C.teal, fontWeight:700, padding:'0 3px', textAlign:'center', lineHeight:1.2 }}>
                            {b.student_name?.split(' ')[0]}
                          </span>
                        </>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop:12, display:'flex', gap:16, fontSize:11, color:C.grey, flexWrap:'wrap' }}>
            {Object.entries(STATUS_COLOR).map(([s, c]) => (
              <span key={s} style={{ display:'flex', alignItems:'center', gap:4 }}>
                <span style={{ width:10, height:10, borderRadius:2, background:STATUS_BG[s], border:`1px solid ${c}`, display:'inline-block' }}/>
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── Booking Management Modal ── */}
      {selected && (
        <div onClick={closeModal} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
          <div onClick={e => e.stopPropagation()} style={{
            background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:500,
            maxHeight:'90vh', overflowY:'auto', boxShadow:'0 8px 40px rgba(0,0,0,0.25)',
          }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h3 style={{ margin:0, fontSize:17, fontWeight:700, color:C.navy }}>
                {editMode ? '✏️ Edit Booking' : '📋 Booking Details'}
              </h3>
              <button onClick={closeModal} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:C.grey, lineHeight:1 }}>×</button>
            </div>

            {/* Always visible info */}
            <div style={{ background:C.bg, borderRadius:10, padding:'12px 16px', marginBottom:20 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                {[
                  ['👤 Student', selected.student_name],
                  ['📅 Date', selected.booking_date],
                  ['🕐 Time', fmt12(selected.time_slot)],
                  ['⏱ Duration', `${editMode ? editData.duration_minutes : selected.duration_minutes || 90} min`],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div style={{ fontSize:11, color:C.grey, marginBottom:2 }}>{k}</div>
                    <div style={{ fontSize:13, fontWeight:600, color:C.navy }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>

            {editMode ? (
              /* Edit Form */
              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

                {/* Status */}
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:5 }}>Status</label>
                  <select value={editData.status} onChange={e => setEditData(p => ({...p, status: e.target.value}))}
                    style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13 }}>
                    {['pending','confirmed','completed','cancelled'].map(s => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>
                    ))}
                  </select>
                </div>

                {/* Assign Tutor */}
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:5 }}>Assign Tutor</label>
                  <select value={editData.tutor_id} onChange={e => setEditData(p => ({...p, tutor_id: e.target.value}))}
                    style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13 }}>
                    <option value="">— Select tutor —</option>
                    {tutors.map(t => (
                      <option key={t.id} value={t.id}>{t.full_name}</option>
                    ))}
                  </select>
                </div>

                {/* Delivery Mode */}
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:5 }}>Delivery Mode</label>
                  <div style={{ display:'flex', gap:8 }}>
                    {[['physical','📍 In Person'], ['remote','🎥 Online']].map(([val, label]) => (
                      <button key={val} onClick={() => setEditData(p => ({...p, delivery_mode: val}))} style={{
                        flex:1, padding:'8px 12px', borderRadius:8,
                        border: `1.5px solid ${editData.delivery_mode===val ? C.teal : C.border}`,
                        background: editData.delivery_mode===val ? C.bg : '#fff',
                        color: editData.delivery_mode===val ? C.teal : C.grey,
                        cursor:'pointer', fontSize:13, fontWeight:600,
                      }}>{label}</button>
                    ))}
                  </div>
                </div>

                {/* Video Link (shown when remote) */}
                {editData.delivery_mode === 'remote' && (
                  <>
                    <div>
                      <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:5 }}>Video Platform</label>
                      <select value={editData.video_platform} onChange={e => setEditData(p => ({...p, video_platform: e.target.value}))}
                        style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13 }}>
                        {PLATFORMS.map(p => (
                          <option key={p.value} value={p.value}>{p.icon} {p.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:5 }}>Meeting Link (URL)</label>
                      <input type="url" value={editData.video_link}
                        onChange={e => setEditData(p => ({...p, video_link: e.target.value}))}
                        placeholder="https://meet.google.com/xxx-xxxx-xxx"
                        style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, boxSizing:'border-box' }}
                      />
                    </div>
                  </>
                )}

                {/* Duration */}
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:5 }}>Duration (minutes)</label>
                  <select value={editData.duration_minutes} onChange={e => setEditData(p => ({...p, duration_minutes: parseInt(e.target.value)}))}
                    style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13 }}>
                    {[45, 60, 90, 120].map(m => <option key={m} value={m}>{m} min</option>)}
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.grey, marginBottom:5 }}>Notes</label>
                  <textarea value={editData.notes} onChange={e => setEditData(p => ({...p, notes: e.target.value}))}
                    rows={2} placeholder="Internal notes..."
                    style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, resize:'vertical', boxSizing:'border-box' }}
                  />
                </div>

                <div style={{ display:'flex', gap:8, marginTop:4 }}>
                  <button onClick={() => setEditMode(false)} style={{
                    flex:1, padding:'10px', borderRadius:8, border:`1px solid ${C.border}`,
                    background:'#fff', color:C.grey, cursor:'pointer', fontWeight:600, fontSize:14,
                  }}>Cancel</button>
                  <button onClick={saveEdit} disabled={saving} style={{
                    flex:2, padding:'10px', borderRadius:8, border:'none',
                    background: saving ? '#9CA3AF' : C.teal, color:'#fff',
                    cursor: saving ? 'default' : 'pointer', fontWeight:700, fontSize:14,
                  }}>{saving ? 'Saving...' : '💾 Save Changes'}</button>
                </div>
              </div>
            ) : (
              /* View Mode */
              <div>
                <div style={{ display:'flex', flexDirection:'column', gap:0, marginBottom:16 }}>
                  {[
                    ['Tutor', selected.tutor_name || '—'],
                    ['Status', selected.status],
                    ['Delivery', selected.delivery_mode || 'physical'],
                    ['Platform', selected.video_platform || '—'],
                    ['Video Link', selected.video_link || '—'],
                    ['Plan Type', selected.plan_type || 'human'],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'9px 0', borderBottom:`1px solid ${C.border}` }}>
                      <span style={{ color:C.grey, fontSize:13 }}>{k}</span>
                      <span style={{ fontWeight:600, fontSize:13,
                        color: k==='Status' ? (STATUS_COLOR[v]||C.navy) : C.navy,
                        textTransform: k==='Status'||k==='Delivery' ? 'capitalize' : 'none',
                        maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                      }}>{v}</span>
                    </div>
                  ))}
                </div>

                {selected.notes && (
                  <div style={{ background:C.bg, borderRadius:8, padding:'10px 14px', fontSize:13, color:C.navy, marginBottom:16 }}>
                    <strong>Notes:</strong> {selected.notes}
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {selected.status === 'pending' && (
                    <button onClick={() => quickAction(selected, 'confirm')} disabled={saving} style={{
                      flex:1, padding:'10px', borderRadius:8, border:'none',
                      background: C.green, color:'#fff', cursor:'pointer', fontWeight:700, fontSize:13, minWidth:100,
                    }}>✓ Confirm</button>
                  )}
                  <button onClick={() => openEdit(selected)} style={{
                    flex:2, padding:'10px', borderRadius:8, border:`1px solid ${C.teal}`,
                    background: C.bg, color: C.teal, cursor:'pointer', fontWeight:600, fontSize:13,
                  }}>✏️ Edit / Assign Tutor & Link</button>
                  <button onClick={() => { closeModal(); openAssignModal(selected); }} style={{
                    flex:1, padding:'10px', borderRadius:8, border:`1px solid ${C.purple}`,
                    background:'#F5F3FF', color:C.purple, cursor:'pointer', fontWeight:600, fontSize:13, minWidth:80,
                  }}>📚 Assign Lesson</button>
                  {selected.status !== 'cancelled' && selected.status !== 'completed' && (
                    <button onClick={() => quickAction(selected, 'cancel')} disabled={saving} style={{
                      padding:'10px 14px', borderRadius:8, border:`1px solid ${C.red}`,
                      background:'#FFF5F5', color:C.red, cursor:'pointer', fontWeight:600, fontSize:13,
                    }}>✕</button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Assign Lesson Modal ── */}
      {assignModal && (() => {
        const SKILL_COLORS = {grammar:'#6366F1',vocabulary:'#059669',reading:'#F59E0B',writing:'#EF4444',speaking:'#00B4D8'};
        const SKILL_BG = {grammar:'#EEF2FF',vocabulary:'#ECFDF5',reading:'#FFFBEB',writing:'#FFF1F2',speaking:'#F0F7FA'};
        const mats = (assignModal.suggestions || []).filter(m => {
          const matchSkill = !assignSkill || m.skill_type === assignSkill;
          const matchQ = !assignSearch || m.title.toLowerCase().includes(assignSearch.toLowerCase()) || m.topic_tag.includes(assignSearch.toLowerCase());
          return matchSkill && matchQ;
        });
        const skills = [...new Set((assignModal.suggestions||[]).map(m => m.skill_type))];
        return (
          <div onClick={() => setAssignModal(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100, padding:16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:560, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 8px 40px rgba(0,0,0,0.25)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <div>
                  <h3 style={{ margin:0, fontSize:17, fontWeight:700, color:C.navy }}>📚 Assign Lesson Materials</h3>
                  <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>{assignModal.booking.student_name} · Level {assignModal.student_level}</div>
                </div>
                <button onClick={() => setAssignModal(null)} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:C.grey }}>×</button>
              </div>

              {/* Filter bar */}
              <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
                <input value={assignSearch} onChange={e => setAssignSearch(e.target.value)} placeholder="Search materials..."
                  style={{ flex:1, minWidth:140, padding:'7px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13 }} />
                <select value={assignSkill} onChange={e => setAssignSkill(e.target.value)}
                  style={{ padding:'7px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13 }}>
                  <option value="">All skills</option>
                  {skills.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                </select>
              </div>

              {/* Selected count */}
              {assignSelected.length > 0 && (
                <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, padding:'8px 12px', marginBottom:12, fontSize:13, color:'#065F46', fontWeight:600 }}>
                  ✓ {assignSelected.length} material{assignSelected.length>1?'s':''} selected
                </div>
              )}

              {/* Material list */}
              <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:340, overflowY:'auto', marginBottom:16 }}>
                {mats.length === 0 ? (
                  <div style={{ textAlign:'center', padding:20, color:C.grey, fontSize:13 }}>No materials found for this filter</div>
                ) : mats.map(m => {
                  const checked = assignSelected.includes(m.id);
                  return (
                    <div key={m.id} onClick={() => toggleAssign(m.id)} style={{
                      display:'flex', alignItems:'flex-start', gap:10, padding:'10px 14px', borderRadius:10, cursor:'pointer',
                      background: checked ? '#F0FDF4' : '#FAFAFA',
                      border: `1.5px solid ${checked ? '#BBF7D0' : C.border}`,
                      transition:'all 0.12s',
                    }}>
                      <input type="checkbox" checked={checked} readOnly style={{ marginTop:2, cursor:'pointer' }} />
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:600, fontSize:13, color:C.navy, marginBottom:3 }}>{m.title}</div>
                        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                          <span style={{ fontSize:11, padding:'2px 8px', borderRadius:12, background:SKILL_BG[m.skill_type]||'#F3F4F6', color:SKILL_COLORS[m.skill_type]||C.grey, fontWeight:600 }}>
                            {m.skill_type}
                          </span>
                          <span style={{ fontSize:11, padding:'2px 8px', borderRadius:12, background:'#EFF6FF', color:'#1D4ED8', fontWeight:600 }}>{m.cefr_level}</span>
                          {m.theme && <span style={{ fontSize:11, color:C.grey }}>{m.theme}</span>}
                        </div>
                        {m.description && <div style={{ fontSize:12, color:C.grey, marginTop:4, lineHeight:1.4 }}>{m.description.slice(0,80)}{m.description.length>80?'...':''}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => setAssignModal(null)} style={{ flex:1, padding:'10px', border:`1px solid ${C.border}`, borderRadius:8, background:'#fff', color:C.grey, cursor:'pointer', fontWeight:600 }}>Cancel</button>
                <button onClick={saveAssignment} disabled={assignSaving} style={{ flex:2, padding:'10px', background: assignSaving ? '#9CA3AF' : C.purple, color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor: assignSaving ? 'default' : 'pointer', fontSize:14 }}>
                  {assignSaving ? 'Saving...' : `💾 Save Assignment`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </Layout>
  );
}
